import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Brain, Bookmark, BookmarkCheck, Zap } from 'lucide-react';
import { useSavedChallenges } from './hooks/useSavedChallenges';
import { useChallengeStorage } from './hooks/useChallengeStorage';
import AuthModal from './auth-layout-pages/AuthModal';
import GenericModal from './GenericModal';
import useCharacters from './hooks/useCharacters';
import Sidebar from './Sidebar';
import "./ChallengesPage.css";

const TaskCard = ({ task, onSolve, isSaved, toggleSave }) => {
  const hasTestCases = task.testCases?.length > 0;
  const hasRemarks = !!task.remarks;

  return (
    <div className="task-card" onClick={() => onSolve(task)}>
      {/* ── Header row: topic + difficulty + save ── */}
      <div className="task-header-challenge">
        <div className="language-challenge">
          <Brain size={16} className="icon-neutral" />
          <span>{task.topic || 'General'}</span>
        </div>
        <div className="difficulty-challenge">
          <span className={`difficulty-badge-challenge ${task.difficulty?.toLowerCase().replace(/\s+/g, '-')}`}>
            {task.difficulty}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); toggleSave(task); }}
            className="save-btn"
            aria-label={isSaved(task) ? 'Unsave challenge' : 'Save challenge'}
          >
            {isSaved(task)
              ? <BookmarkCheck size={18} className="icon-accent" />
              : <Bookmark size={18} className="icon-muted" />
            }
          </button>
        </div>
      </div>

      {/* ── Title + description ── */}
      <h3>{task.title}</h3>
      <p className="description">{task.description}</p>

      {/* ── Test cases preview ── */}
      {hasTestCases && (
        <div className="card-rules-preview">
          <div className="card-rules-header">
            <Zap size={13} className="icon-accent" />
            <span className="card-rules-count">{task.testCases.length} rule{task.testCases.length !== 1 ? 's' : ''} to follow</span>
          </div>
          <p className="card-rules-first">"{task.testCases[0]}"</p>
        </div>
      )}

      {/* ── Additional notes ── */}
      {hasRemarks && (
        <div className="card-remarks">
          <span className="card-remarks-label">💡</span>
          <p className="card-remarks-text">{task.remarks}</p>
        </div>
      )}
    </div>
  );
};

const INITIAL_COUNT = 12;
const LOAD_MORE_COUNT = 12;

const ChallengesPage = ({ isPremium = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentDifficulty, setCurrentDifficulty] = useState('All');
  const [currentTopic, setCurrentTopic] = useState('All');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const { isSaved, saveChallenge, removeSavedChallenge } = useSavedChallenges();
  const { canAttemptChallenge, userPlan, PLANS, loading } = useChallengeStorage();
  const { getCharacterImage } = useCharacters();

  const currentLanguage = 'Prompt Engineering';
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (location.state?.error) {
      setErrorMessage(location.state.error);
      setIsLimitModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
  }, [currentDifficulty, currentTopic]);

  const challenges = require('./challenges/promptEng.json');
  const topics = ['All', ...new Set(challenges.map(t => t.topic).filter(Boolean))];

  const filteredTasks = challenges.filter(task =>
    (currentDifficulty === 'All' || task.difficulty === currentDifficulty) &&
    (currentTopic === 'All' || task.topic === currentTopic)
  );

  const visibleTasks = filteredTasks.slice(0, visibleCount);
  const hasMore = visibleCount < filteredTasks.length;

  const toggleSave = async (task) => {
    if (isSaved(currentLanguage, task.title)) {
      await removeSavedChallenge(currentLanguage, task.title);
    } else {
      await saveChallenge({ ...task, language: currentLanguage });
    }
  };

  const { allowed: canAttempt, reason } = canAttemptChallenge();

  const handleSolve = (task) => {
    if (!user) return setIsAuthModalOpen(true);
    if (!canAttempt) {
      setErrorMessage(reason);
      setIsLimitModalOpen(true);
      return;
    }
    const encodedTitle = encodeURIComponent(task.title.replace(/\s+/g, '-'));
    navigate(`/challenge/${currentLanguage}/${encodedTitle}`);
  };

  return (
    <div className="pcd-shell">
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        currentTopic={currentTopic}
        setCurrentTopic={setCurrentTopic}
        currentDifficulty={currentDifficulty}
        setCurrentDifficulty={setCurrentDifficulty}
        topics={topics}
      />

      {sidebarOpen && (
        <div className="pcd-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <div className={`pcd-main ${sidebarOpen ? 'sidebar-is-open' : ''}`}>
        <button className="pcd-hamburger" onClick={() => setSidebarOpen(v => !v)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>

        <div className="challenges-content">
          {filteredTasks.length === 0 ? (
            <div className="no-challenges">
              <p>No challenges found for these filters.</p>
              <button
                className="reset-filters-btn"
                onClick={() => { setCurrentDifficulty('All'); setCurrentTopic('All'); }}
              >
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="challenges-grid">
                {visibleTasks.map((task, i) => (
                  <TaskCard
                    key={i}
                    task={task}
                    onSolve={handleSolve}
                    isSaved={(t) => isSaved(currentLanguage, t.title)}
                    toggleSave={toggleSave}
                  />
                ))}
              </div>

              {hasMore && (
                <button
                  className="load-more-btn"
                  onClick={() => setVisibleCount(v => v + LOAD_MORE_COUNT)}
                >
                  Load more 
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <GenericModal
        isOpen={isLimitModalOpen}
        onClose={() => setIsLimitModalOpen(false)}
        title="Limit Reached"
        message={errorMessage}
        characterImage={getCharacterImage('sad')}
      />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </div>
  );
};

export default ChallengesPage;